/**
 * Excel: Reinstate withdrawn application — posts `intent=reinstate`.
 */
import { ArrowRight, RotateCcw } from 'lucide-react'
import { useFetcher } from 'react-router'
import type {
	ApplicationSummary,
	DetailActionResult,
} from '#app/utils/application-detail-v2/types'
import {
	ActionResultMarker,
	DetailDialog,
	InlineActionError,
	PillButton,
} from './dialog-bits'

export function ReinstateDialog({
	open,
	onOpenChange,
	application,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const busy = fetcher.state !== 'idle'

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Application reinstated"
				onSuccess={() => onOpenChange(false)}
			/>
			<DetailDialog
				open={open}
				onOpenChange={onOpenChange}
				title="Reinstate Application"
				description="Restores this withdrawn application to active so you can continue reviewing it."
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl"
			>
				<InlineActionError fetcher={fetcher} open={open} />
				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton
						tone="dark"
						disabled={busy}
						onClick={() => {
							void fetcher.submit(
								{ intent: 'reinstate', applicationId: application.id },
								{ method: 'post' },
							)
						}}
					>
						{busy ? 'Reinstating...' : 'Reinstate'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
